import { ConfigOptions, config } from 'lib/vendor/terrific/config';
import {
	Skin,
	Registry,
	Application,
	RegistryEntry,
	SimplifiedRegistry,
	transformRegistryToFull,
} from 'lib/vendor/terrific/Application';
import { extend, Extend, mergeRegistries } from 'lib/vendor/terrific/utils/extend';

import * as $ from 'jquery';

import { bootstrapTerrific } from 'lib/vendor/terrific/utils/bootstrap';
import { Module } from 'lib/vendor/terrific/Module';
import { Sandbox } from 'lib/vendor/terrific/Sandbox';
import { initNewModules } from 'lib/util/helpers';

export interface LegacyModule<M extends Module, S extends M> {
	new($ctx : JQuery, sandbox : Sandbox, id : number) : M
	[name : string] : Skin<M, S>;
}

export interface LegacyRegistry {
	extend : Extend;
	[name : string] : LegacyModule<Module, Module> | Extend;
}

export function legacyRegistryToModern(legacyRegistry : LegacyRegistry) : Registry {
	const registry : Registry = {};

	for(const moduleName in legacyRegistry) {
		const mod = legacyRegistry[moduleName] as LegacyModule<Module, Module>;
		const regEntry : RegistryEntry = {
			module: mod,
			skins: {},
		};
		for(const skinName in mod) {
			const skin : Skin<Module, Module> = mod[skinName];
			// tslint:disable-next-line:strict-type-predicates
			if(typeof skin !== 'function' || !/^[A-Z0-9]$/.test(skinName[0])) {
				continue;
			}

			// We could use `delete mod[skinName]` here but it might remove too much
			// (if some non-skin functions are exported on the Module constructor)

			// Add the skin to the skin registry
			regEntry.skins[skinName] = skin;
		}

		registry[moduleName] = regEntry;
	}

	return registry;
}


function bootstrapWithLegacy(
	$page = $('html'),
	modernRegistry : SimplifiedRegistry = {},
	legacyRegistry = Tc.Module,
	debug = !!(window.location.search + window.location.hash).match(/\bdevconsole\b/),
) : Application {
	// Merge legacy and modern registries
	const registry = mergeRegistries(legacyRegistryToModern(legacyRegistry), transformRegistryToFull(modernRegistry));

	return bootstrapTerrific(
		$page,
		registry,
		debug,
	);
}

export function createLegacyRegistry() : LegacyRegistry {
	const registry = {
		get extend() {
			return extend;
		},
	};
	Object.defineProperty(registry, 'extend', { enumerable: false });

	return registry;
}

function resetLegacyRegistry(registry : LegacyRegistry) : LegacyRegistry {
	const result = createLegacyRegistry();

	for(const module in registry) {
		result[module] = registry[module];
		// tslint:disable-next-line:no-dynamic-delete
		delete registry[module];
	}

	return result;
}

function amendModulesSinceInit(
	application = window.application,
	legacyRegistry = Tc.Module,
	initialRegistry : LegacyRegistry | null = null,
) {
	application.amendRegistry(legacyRegistryToModern(legacyRegistry));

	if(!initialRegistry) {
		return;
	}

	Object.assign(initialRegistry, resetLegacyRegistry(legacyRegistry));
}

export function legacyBootstrapDeferred(
	modernRegistry : SimplifiedRegistry = {},
	$page = $('html'),
	legacyRegistry : LegacyRegistry = Tc.Module,
) : [Application, LegacyRegistry] {
	const application = bootstrapWithLegacy($page, modernRegistry, legacyRegistry);
	// Reset the registry for the next round
	const initialRegistry : LegacyRegistry = resetLegacyRegistry(legacyRegistry);

	// Shim addModules to take modules into account that were added to the registry after initialization
	// Previously, these were added automatically because Tc would not clone the registry it was given.
	// tslint:disable-next-line:no-unbound-method
	const origAddModules = application.sandbox.addModules;
	application.sandbox.addModules = function addModules($ctx? : JQuery) {
		amendModulesSinceInit(application, legacyRegistry, initialRegistry);

		return origAddModules.call(this, $ctx);
	};

	$(() => {
		application.sandbox.addModules = origAddModules;
		amendModulesSinceInit(application, legacyRegistry, initialRegistry);
		// Put back all modules onto initial registry (probably Tc.Module)
		Object.assign(legacyRegistry, initialRegistry);

		// Initialize the modules.
		initNewModules($page);
	});

	return [application, initialRegistry];
}

// Variant of legacyBootstrapDeferred that only calls legacyBootstrapDeferred once but still adds modules.
let applicationSingleton : Application | null = null;
let initialRegistrySingleton : LegacyRegistry;
export function legacyBootstrapDeferredSingleton(modernRegistry : SimplifiedRegistry = {}) {
	if (window.application && window.application.sandbox) {
		applicationSingleton = window.application;
	}
	if(applicationSingleton) {
		// Amend the passed modules
		applicationSingleton.amendRegistry(modernRegistry);
		// Amend the modules that have since been registered on Tc.Module
		amendModulesSinceInit(applicationSingleton, Tc.Module, initialRegistrySingleton);

		return applicationSingleton;
	}
	[applicationSingleton, initialRegistrySingleton] = legacyBootstrapDeferred(modernRegistry, undefined, Tc.Module);
	window.application = applicationSingleton;

	return applicationSingleton;
}


interface LegacyTc {
	Config : ConfigOptions
	Module : LegacyRegistry
	Application : typeof Application
	$ : typeof $
}

declare global {
	interface Window {
		Tc : LegacyTc;
		application : Application;
		scGlobals : Record<string, any>;
	}
}

// tslint:disable-next-line:variable-name
const Tc : LegacyTc = {
	$,
	Application,
	Config: config,
	Module: createLegacyRegistry(),
};

window.Tc = Tc;
export default Tc;



// WEBPACK FOOTER //
// typescript://../clientlibs/js/bootstrap/load-terrific.ts